import { useEffect, useRef, useState } from 'react';

import { BackrsForm, BackrsFormField, BackrsFormValues, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useProcessInviteCodeForExistingUserMutation } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { Box, Button, SxProps, Theme, Typography, Link } from '@mui/material';

const STRINGS = appStrings.modules.activationCode;

// Holds the confirmation on screen long enough to read before the flow ends.
const SUCCESS_DELAY_MS = 1500;

const reloadPage = () => window.location.reload();

/** Failure body the invite-code endpoint returns: displayable prose plus a machine reason. */
type InviteCodeErrorBody = Readonly<{
  error: string;
  code?: string;
}>;

const hasErrorProse = (body: unknown): body is InviteCodeErrorBody =>
  !!body &&
  typeof body === 'object' &&
  typeof (body as { error?: unknown }).error === 'string' &&
  (body as { error: string }).error.trim().length > 0;

/**
 * Reads the prose the endpoint returns as `{ error, code }`. Anything else — a blank or
 * non-string `error`, a transport failure, a non-JSON body — falls back to generic copy,
 * so the message slot is never empty once it renders.
 */
const getErrorMessage = (error: unknown): string => {
  const body = (error as { data?: unknown } | undefined)?.data;

  return hasErrorProse(body) ? body.error : STRINGS.errorProcessing;
};

type ActivationCodeInputProps = Readonly<{
  /** Namespaces the link and submit-button ids, so the mount points stay distinguishable. */
  idPrefix: string;
  textAlign?: 'center' | 'left';
  /**
   * Applies the newly granted access once a code is accepted, awaited before the modal
   * closes. Defaults to reloading the page after a beat; a host holding unsaved state
   * should pass a handler that refreshes without navigating. A rejection leaves the modal
   * open showing the refresh-failed copy, so a stale view is never reported as success.
   */
  onSuccess?: () => void | Promise<void>;
}>;

export const ActivationCodeInput = ({
  idPrefix,
  textAlign = 'center',
  onSuccess = reloadPage,
}: ActivationCodeInputProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [didRefreshFail, setDidRefreshFail] = useState(false);
  const [processInviteCode, { isLoading, isSuccess, error, reset }] =
    useProcessInviteCodeForExistingUserMutation();

  // Read through a ref so an unstable callback can't re-run the effect and apply access twice.
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  });

  useEffect(() => {
    // Closing the modal is an exit from the flow, not just a visual dismissal: it cancels
    // the pending apply the same way unmounting does.
    if (!isSuccess || !isModalOpen) {
      return;
    }

    let isActive = true;

    const applyAccess = async () => {
      try {
        await onSuccessRef.current();
      } catch {
        if (isActive) {
          setDidRefreshFail(true);
        }

        return;
      }

      if (!isActive) {
        return;
      }

      setIsModalOpen(false);
    };

    const timer = setTimeout(applyAccess, SUCCESS_DELAY_MS);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [isSuccess, isModalOpen]);

  const handleSubmit = async (data: BackrsFormValues) => {
    const code = data.invite_code?.trim();

    if (!code) {
      return;
    }

    try {
      await processInviteCode({ code }).unwrap();
    } catch (submitError) {
      // Keeps a trace when a rejection carries something the rendered message can't show.
      console.error('Error processing activation code:', submitError);
    }
  };

  const openModal = () => {
    reset();
    setDidRefreshFail(false);
    setIsModalOpen(true);
  };

  const activationCodeField: BackrsFormField = {
    name: 'invite_code',
    placeholder: STRINGS.placeholder,
    type: 'text',
    post_type: 'USER',
    rules: {
      validate: (value: string) => (value?.trim() ? true : STRINGS.codeRequired),
    },
  };

  return (
    <Box sx={containerSx(textAlign)}>
      <Typography variant="text-md-regular">
        {STRINGS.haveCode}{' '}
        <Link
          id={`${idPrefix}-link`}
          sx={linkSx}
          onClick={openModal}
          component="button"
          type="button"
        >
          {STRINGS.enterItHere}
        </Link>
      </Typography>

      <InfoModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        width="sm"
        featuredIcon={{
          iconName: 'UserCircle',
          size: 'lg',
          color: 'Colors/Foreground/fg-secondary',
        }}
        headerContent={<Typography variant="text-xl-semibold">{STRINGS.modalTitle}</Typography>}
        bodyContent={
          <Box sx={formContainerSx}>
            {isSuccess ? (
              <>
                <Typography
                  variant="text-sm-regular"
                  role="alert"
                  color={({ backrs }) =>
                    didRefreshFail
                      ? backrs.colors['Colors/Text/text-error-primary']
                      : backrs.colors['Colors/Text/text-success-primary']
                  }
                  sx={feedbackTextSx}
                >
                  {didRefreshFail ? STRINGS.refreshFailed : STRINGS.success}
                </Typography>
                {didRefreshFail && (
                  <Button
                    id={`${idPrefix}-reload-button`}
                    hierarchy="Primary"
                    size="lg"
                    fullWidth
                    onClick={reloadPage}
                  >
                    {STRINGS.reload}
                  </Button>
                )}
              </>
            ) : (
              <>
                <BackrsForm
                  fields={[activationCodeField]}
                  onSubmit={handleSubmit}
                  buttonLabel={STRINGS.submit}
                  buttonId={`${idPrefix}-submit-button`}
                  isLoading={isLoading}
                />
                {!!error && (
                  <Typography
                    variant="text-sm-regular"
                    role="alert"
                    color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
                    sx={feedbackTextSx}
                  >
                    {getErrorMessage(error)}
                  </Typography>
                )}
              </>
            )}
          </Box>
        }
      />
    </Box>
  );
};

const containerSx =
  (textAlign: 'center' | 'left'): SxProps<Theme> =>
  ({ backrs }) => ({
    padding: backrs.spacing['spacing-md'],
    textAlign,
  });

const linkSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const feedbackTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-sm'],
  textAlign: 'center',
});
